import { Box, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material'
import { colors } from 'constants/theme'
import AlertModal from 'new-components/modal/alert-modal'
import { useTranslation } from 'react-i18next'

export interface PendingOperativeAlert {
  operativeId: number
  operativeCode: string
  operativeName: string
  operativeNameEn?: string
}

export interface PendingProductAlert {
  productId: number
  productName: string
  productCode: string
  productGroup: 'MEDICINE' | 'PRODUCT' | 'MATERIAL' | string
}

export interface PendingAlertsData {
  operatives: PendingOperativeAlert[]
  products: PendingProductAlert[]
}

interface CaseAlertModalProps {
  isOpen: boolean
  alertsData: PendingAlertsData | null
  onClose: () => void
  onContinuePayment: () => void
  onGoBackToTreatment: () => void
}

export default function CaseAlertModal({
  isOpen,
  alertsData,
  onClose,
  onContinuePayment,
  onGoBackToTreatment
}: CaseAlertModalProps) {
  const { t } = useTranslation()

  if (!alertsData) return null

  const hasXrayAlert = alertsData.operatives && alertsData.operatives.length > 0
  const hasMedicineAlert = alertsData.products && alertsData.products.length > 0

  const renderAlertContent = () => (
    <Box sx={{ textAlign: 'left' }}>
      {hasXrayAlert && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography fontWeight={600} fontSize={14} color={colors.themeMainColor}>
              {t('ALERT_MODAL.XRAY_PENDING')}
            </Typography>
          </Box>
          <List dense sx={{ pl: 2 }}>
            {alertsData.operatives.map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  •
                  {/* <WarningAmberIcon sx={{ fontSize: 16, color: '#ff9800' }} /> */}
                </ListItemIcon>
                <ListItemText
                  primary={`${item.operativeName || ''}${item.operativeCode ? ` (${item.operativeCode})` : ''}`}
                  primaryTypographyProps={{ fontSize: 14 }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {hasMedicineAlert && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {/* <MedicationOutlinedIcon sx={{ color: colors.themeMainColor }} /> */}
            <Typography fontWeight={600} fontSize={14} color={colors.themeMainColor}>
              {t('ALERT_MODAL.MEDICINE_PENDING')}
            </Typography>
          </Box>
          <List dense sx={{ pl: 2 }}>
            {alertsData.products.map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  •
                </ListItemIcon>
                <ListItemText
                  primary={`${item.productName || ''} ${item.productCode ? `(${item.productCode})` : ''}`}
                  primaryTypographyProps={{ fontSize: 14 }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  )

  return (
    <AlertModal
      isOpen={isOpen}
      title={t('ALERT_MODAL.TITLE')}
      type="warning"
      handleClose={onClose}
      onCancel={{
        onClick: onGoBackToTreatment,
        title: t('ALERT_MODAL.GO_BACK_TO_TREATMENT')
      }}
      onSubmit={{
        onClick: onContinuePayment,
        title: t('ALERT_MODAL.CONTINUE_PAYMENT')
      }}
    >
      {renderAlertContent()}
    </AlertModal>
  )
}
